/**
 * Control flow handlers (f:*).
 */

import type { FlowStep } from "../types/flow";
import { evaluateCondition } from "../expressions/condition";
import { resolveRecord, resolveValue } from "../expressions/resolve";
import { runSteps } from "../runtime/flow-runner";
import { registerStepHandler } from "../runtime/step-registry";
import type { StepResult } from "../runtime/types";

function asRecord(step: FlowStep): Record<string, unknown> {
 return step as unknown as Record<string, unknown>;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
 return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function snapshotRecord(value: unknown): Record<string, unknown> {
 if (!value || typeof value !== "object" || Array.isArray(value)) {
 return {};
 }

 return { ...(value as Record<string, unknown>) };
}

registerStepHandler("f:if", async (step, ctx): Promise<StepResult | void> => {
 const r = asRecord(step);
 const condition = (r["?"] ?? r["condition"]) as unknown;
 const branchKey = evaluateCondition(condition, ctx) ? "then" : "else";
 const branch = r[branchKey];
 if (Array.isArray(branch)) {
 return await runSteps(branch as readonly FlowStep[], ctx);
 }
});

registerStepHandler("f:switch", async (step, ctx): Promise<StepResult | void> => {
 const r = asRecord(step);
 const value = resolveValue(r["on"], ctx);
 const cases = isPlainObject(r["cases"]) ? (r["cases"] as Record<string, readonly FlowStep[]>) : {};
 const key = String(value ?? "");
 const branch = cases[key] ?? (r["default"] as readonly FlowStep[] | undefined);
 if (Array.isArray(branch)) {
 return await runSteps(branch, ctx);
 }
});

registerStepHandler("f:call", async (step, ctx): Promise<StepResult | void> => {
 const r = asRecord(step);
 const idValue = resolveValue(r["id"], ctx);
 if (typeof idValue !== "string" || !idValue.trim()) {
 throw new Error(`f:call missing flow id`);
 }

 const id = idValue.trim();
 const child = ctx.resolveFlow(id);
 if (!child) {
 throw new Error(`f:call references unknown flow "${id}"`);
 }

 const args = resolveRecord(isPlainObject(r["args"]) ? (r["args"] as Record<string, unknown>) : undefined, ctx);
 const savedInput = snapshotRecord(ctx.getScope("input"));
 const savedParams = snapshotRecord(ctx.getScope("params"));

 // Child flows should see the provided arguments as both `@input`
 // and `@params`. Many flows author their nested steps against
 // `@params.*` (for example, reusable save-and-navigate flows).
 ctx.setInput(args);
 ctx.setParams(args);

 try {
 return await runSteps(child.steps, ctx);
 } finally {
 ctx.setInput(savedInput);
 ctx.setParams(savedParams);
 }
});

registerStepHandler("f:return", (step, ctx): StepResult => {
 const r = asRecord(step);
 const value = resolveValue(r["v"], ctx);
 return { shouldReturn: true, returnValue: value };
});

registerStepHandler("f:noop", () => {
 // intentional no-op
});